/**
 * Settings → Microsoft Sentinel (Enterprise): the data collection rule the platform writes the
 * organisation's security-worthy audit rows to, as this deployment's managed identity or the AIOE
 * API application (docs/agent365.md; Azure refuses a blueprint any role assignment).
 */

import { Button } from '@heroui/react';
import { useState } from 'react';

import type { SentinelDetectionState, SentinelDetectionsView } from '@aioe/shared';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ErrorNote, inputClass, PlainPanel, StatusMark } from './ui.tsx';

export function SentinelPanel() {
  const view = useLoad(() => routes.sentinel(), []);
  const [endpoint, setEndpoint] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const act = async (f: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(true);
    try {
      await f();
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const v = view.data;
  return (
    <PlainPanel title="Microsoft Sentinel">
      <ErrorNote error={error ?? view.error} />
      {v ? (
        <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-y-1.5 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
          <dt className="text-muted">Status</dt>
          <dd>{v.connected ? <StatusMark state="online" label="Connected" /> : <StatusMark state="idle" label="Not connected" />}</dd>
          {v.connected ? (
            <>
              <dt className="text-muted">Rule</dt>
              <dd className="mono break-all">
                {v.ruleId} <span className="text-muted">{v.stream}</span>
              </dd>
              <dt className="text-muted">Endpoint</dt>
              <dd className="mono break-all">{v.endpoint}</dd>
              <dt className="text-muted">Rows sent</dt>
              <dd className="tabular-nums">
                {v.rowsSent}
                {v.lastSentAt ? <span className="text-muted"> · last {timeAgo(v.lastSentAt)}</span> : null}
              </dd>
              {v.lastError ? (
                <>
                  <dt className="text-muted">Last problem</dt>
                  <dd className="text-danger">{v.lastError}</dd>
                </>
              ) : null}
            </>
          ) : null}
        </dl>
      ) : null}
      {v && !v.connected ? (
        <div className="mt-3 grid gap-3">
          <p className="text-sm text-muted">Audit rows worth a detection (refusals, errors, every data-protection decision) are sent to your Sentinel workspace as rows of MojoUpSecurityEvents_CL, each with its place and hash in the audit chain. Deploy the shipped table, rule and detections first, naming this deployment’s managed identity (or, outside Azure, the AIOE API application) as the sender: it gets the Monitoring Metrics Publisher role on the rule.</p>
          <label className="grid gap-1 text-xs text-muted">
            Logs ingestion endpoint
            <input className={`${inputClass} mono`} value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://mojoup-dce-xxxx.australiaeast-1.ingest.monitor.azure.com" spellCheck={false} />
          </label>
          <label className="grid gap-1 text-xs text-muted">
            Data collection rule immutable id
            <input className={`${inputClass} mono`} value={ruleId} onChange={(e) => setRuleId(e.target.value)} placeholder="dcr-…" spellCheck={false} />
          </label>
          <div>
            <Button size="sm" isPending={busy} isDisabled={!endpoint.trim() || !ruleId.trim()} onPress={() => void act(() => routes.connectSentinel({ endpoint: endpoint.trim(), ruleId: ruleId.trim(), stream: 'Custom-MojoUpSecurityEvents' }))}>
              Connect
            </Button>
          </div>
        </div>
      ) : v?.connected ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" isPending={busy} onPress={() => void act(() => routes.disconnectSentinel())}>
            Disconnect
          </Button>
          <span className="text-xs text-muted">A backfill: Audit, export, format Sentinel.</span>
        </div>
      ) : null}
      {v?.connected ? <Detections /> : null}
    </PlainPanel>
  );
}

const STATE: Record<SentinelDetectionState, { state: 'online' | 'medium' | 'idle'; label: string }> = {
  current: { state: 'online', label: 'Installed' },
  outdated: { state: 'medium', label: 'Out of date' },
  missing: { state: 'idle', label: 'Not installed' },
  unknown: { state: 'idle', label: 'Not checked' },
};

/**
 * The Mojo Up detections as analytics rules in the workspace: which are installed and current, one button to
 * install or update them all as this deployment, and the same rules as a template for Azure's own tools.
 */
function Detections() {
  const view = useLoad(() => routes.sentinelDetections(), []);
  const [workspace, setWorkspace] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState<SentinelDetectionsView>();
  const v = answered ?? view.data;
  const ws = workspace ?? v?.workspace ?? '';
  const install = async () => {
    setError(undefined);
    setBusy(true);
    try {
      setAnswered(await routes.installSentinelDetections({ workspace: ws.trim() }));
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const due = v?.detections.filter((d) => d.state !== 'current').length ?? 0;
  return (
    <section className="mt-6 grid gap-3">
      <div>
        <h3 className="text-sm font-medium">Detections</h3>
        <p className="text-sm text-muted">Analytics rules that turn these rows into Sentinel incidents: decoy keys, secrets in changes, prompt injection, tampering and more. Installing them needs the Microsoft Sentinel Contributor role on the workspace for this deployment’s managed identity (or, outside Azure, the AIOE API application).</p>
      </div>
      <ErrorNote error={error ?? view.error ?? v?.problem} />
      <label className="grid gap-1 text-xs text-muted">
        Log Analytics workspace resource id
        <input className={`${inputClass} mono`} value={ws} onChange={(e) => setWorkspace(e.target.value)} placeholder="/subscriptions/…/resourceGroups/…/providers/Microsoft.OperationalInsights/workspaces/…" spellCheck={false} />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" isPending={busy} isDisabled={!ws.trim()} onPress={() => void install()}>
          {v?.workspace && due === 0 ? 'Check again' : v?.workspace ? 'Update detections' : 'Install detections'}
        </Button>
        <Button size="sm" variant="ghost" onPress={() => void routes.downloadSentinelTemplate().catch((e: unknown) => setError(problemText(e)))}>
          Download template
        </Button>
        {v?.installedAt ? <span className="text-xs text-muted">Installed {timeAgo(v.installedAt)}</span> : null}
      </div>
      {v ? (
        <ul className="grid gap-2">
          {v.detections.map((d) => (
            <li key={d.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 text-sm">
              <div className="min-w-0">
                <div>
                  {d.name.replace(/^Mojo Up: /, '')} <span className="text-xs text-muted">· {d.severity}</span>
                </div>
                <div className="text-xs text-muted">{d.description}</div>
              </div>
              {d.enabled === false && d.state !== 'missing' ? <StatusMark state="disabled" label="Turned off" /> : <StatusMark state={STATE[d.state].state} label={STATE[d.state].label} />}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-xs text-muted">
        To deploy with Azure’s own tools instead: <span className="mono break-all">{v?.template.command}</span>
      </p>
    </section>
  );
}
